<template>
  <div>
    <input v-if="options.length > 8" ref="searchEl" v-model="query" type="text" placeholder="Search…" data-filter-search
      @keydown="onKeydown"
      class="sticky top-0 z-10 w-full px-3 py-2 text-xs bg-surface dark:bg-surface-dark border-b border-stroke dark:border-stroke-dark outline-none text-fg-base dark:text-fg-base-dark placeholder:text-fg-faint" />
    <button
      v-for="(opt, i) in visible" :key="opt.value" :ref="el => { optionEls[i] = el }"
      @click="$emit('toggle', opt.value)"
      @mouseenter="highlightedIndex = i"
      class="w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition-colors scroll-mt-9 hover:bg-surface-muted dark:hover:bg-surface-hover-dark"
      :class="[
        opt.active ? 'font-semibold text-fg-base dark:text-fg-base-dark' : 'text-fg-secondary dark:text-fg-secondary-dark',
        i === highlightedIndex ? 'bg-surface-muted dark:bg-surface-hover-dark' : '',
      ]"
    >
      <span v-if="opt.dot" class="w-2 h-2 rounded-full flex-shrink-0" :style="{ background: opt.dot }" />
      <span class="flex-1 truncate">{{ opt.label }}</span>
      <CheckIcon v-if="opt.active" class="h-3.5 w-3.5 flex-shrink-0 text-blue-600 dark:text-blue-400" />
    </button>

    <button v-if="!hideClear && activeCount > 0" @click="$emit('clear')"
      class="w-full px-3 py-2 text-xs text-left text-fg-faint dark:text-fg-faint-dark hover:text-fg-secondary dark:hover:text-fg-base-dark border-t border-stroke dark:border-stroke-dark transition-colors underline underline-offset-2">
      Clear
    </button>
  </div>
</template>

<script setup>
import { computed, ref, watch, nextTick, onMounted } from 'vue'
import { CheckIcon } from '@heroicons/vue/24/outline'

const props = defineProps({
  options:   { type: Array,   default: () => [] },  // [{ value, label, active, dot? }]
  hideClear: { type: Boolean, default: false },      // for single-select lists (e.g. Day/Week/Month) — always exactly one active, so "Clear" doesn't apply
})
const emit = defineEmits(['toggle', 'clear'])

const query = ref('')
const searchEl = ref(null)
const highlightedIndex = ref(0)

// Case- and accent-insensitive: "tea" matches "Téa" (NFD splits é into e + a combining mark, which is then stripped).
const fold = str => String(str).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
const visible = computed(() => {
  const q = fold(query.value.trim())
  return q ? props.options.filter(o => fold(o.label).includes(q)) : props.options
})
const activeCount = computed(() => props.options.filter(o => o.active).length)

// Reset only when the search text changes — toggling an option also produces a new
// `visible` array (its `active` flag flips), and resetting on that jumped the highlight
// back to the top after every Enter.
watch(query, () => { highlightedIndex.value = 0 })
watch(visible, v => { if (highlightedIndex.value >= v.length) highlightedIndex.value = Math.max(0, v.length - 1) })

// Keyboard moves keep the highlighted row inside the dropdown's scroll area.
const optionEls = []  // indexed by position in `visible` (v-for ref arrays aren't order-guaranteed)
const moveHighlight = i => {
  highlightedIndex.value = i
  nextTick(() => optionEls[i]?.scrollIntoView({ block: 'nearest' }))
}

onMounted(() => {
  if (searchEl.value) nextTick(() => searchEl.value.focus())
})

const onKeydown = e => {
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    if (visible.value.length) moveHighlight((highlightedIndex.value + 1) % visible.value.length)
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    if (visible.value.length) moveHighlight((highlightedIndex.value - 1 + visible.value.length) % visible.value.length)
  } else if (e.key === 'Escape' && query.value) {
    // First Esc clears the search so another name can be typed; the parent dropdown
    // skips non-empty search boxes, so only a second Esc (empty box) closes it.
    e.preventDefault()
    e.stopPropagation()
    query.value = ''
  } else if ((e.key === 'Backspace' || e.key === 'Delete') && !query.value) {
    // Backspace/Delete in an empty search box clears every selection, like the Clear button.
    e.preventDefault()
    if (!props.hideClear && activeCount.value > 0) emit('clear')
  } else if (e.key === 'Enter') {
    e.preventDefault()
    const opt = visible.value[highlightedIndex.value]
    if (!opt) return
    emit('toggle', opt.value)
    // After a search-and-pick, clear the search for the next name, keeping the picked
    // option highlighted (and scrolled to) in the now-unfiltered list.
    if (query.value) {
      query.value = ''
      nextTick(() => moveHighlight(Math.max(0, visible.value.findIndex(o => o.value === opt.value))))
    }
  }
}
</script>
